import {
  isAuth,
  isSectionWorthy,
} from 'components/common/configPanel/actionSetup';
import { ProviderType } from 'components/common/ObjectTypes';
import { getRouteKind } from 'components/common/routeKind';

// Reference actions are stateless option lookups (RFC: reference-data actions);
// they are invoked by form widgets, never configured, so they must not render
// as configuration forms anywhere.
//
// Credentials are exempt from the direction filter: auth is not a direction, so
// a connector that types its auth action `pull` or `push` still has to collect
// it. Without this a creation screen could neither show those fields nor block
// on them, and a Connection could be created with no credentials at all
// (GUNDI-5660 — product call: an integration that cannot authenticate is the one
// thing creation must stop for).
export const isConfigurableAction = (
  item: any,
  exclude: 'push' | 'pull',
): boolean =>
  !!item &&
  item.type !== 'reference' &&
  (isAuth(item) || item.type !== exclude);

export const filterConfigurableActions = (
  actions: any[] | undefined | null,
  exclude: 'push' | 'pull',
) => (actions ?? []).filter((item) => isConfigurableAction(item, exclude));

// What a creation screen shows, given the directions data can move on it:
// credentials, plus the flow actions for those directions. Reference lookups and
// on-demand helpers are never included — they are invoked by form widgets or run
// by hand, so nothing about them is part of setting a record up, and their
// required fields would block creation for configuration the user was never
// asked to provide (GUNDI-5697).
//
// Kept here so a screen and its validator cannot disagree about which sections
// exist (GUNDI-5642 follow-up: required auth fields went unenforced because the
// two lists were computed apart).
// Executability earns a section (see isSectionWorthy) but not an exemption from
// the direction filter: a creation screen shows the directions it is for, so an
// executable action of another type — a reference lookup, an on-demand helper —
// stays out of it and is reached on the flow map instead (product call).
const setupActions = (
  actions: any[] | undefined | null,
  directions: string[],
) =>
  (actions ?? []).filter(
    (a: any) =>
      !!a && isSectionWorthy(a) && (isAuth(a) || directions.includes(a.type)),
  );

// A Connection in its own right has no direction yet — its role comes from the
// Routes it later joins — so it shows both. Picking a technology under "Can send
// data to Gundi" and then finding no section for the pull action that provides
// that capability is the failure this avoids.
export const connectionSetupActions = (actions: any[] | undefined | null) =>
  setupActions(actions, ['pull', 'push']);

// A destination added from inside a Route only ever receives, so the sections
// that send data would be asking for configuration its role never uses.
export const destinationSetupActions = (actions: any[] | undefined | null) =>
  setupActions(actions, ['push']);

// Route creation is the pull side: the provider brings data in.
// A webhook provider calls Gundi rather than being called, so on this side only a
// pull action could use its credentials. With no pull section to serve, they belong
// to the type's destination role and are asked for where that role is set up.
export const routeSetupActions = (
  actions: any[] | undefined | null,
  providerType?: ProviderType,
) => {
  const shown = setupActions(actions, ['pull']);
  if (!providerType || getRouteKind(providerType) !== 'webhook') return shown;
  return shown.some((a: any) => !isAuth(a)) ? shown : [];
};
